import { Transition } from "@headlessui/react";
import { PlusIcon, MinusIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type QuantityInputProps = {
  value: number;
  checkout?: boolean;
  onChange: (value: number) => unknown;
};

const QuantityInput = ({
  value,
  onChange,
  checkout = false,
}: QuantityInputProps) => {
  const [showValue, setShowValue] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const prevValueRef = useRef(value);
  const animationRef = useRef<ReturnType<typeof setTimeout>>();

  const changeValue = (newValue: number) => {
    if (checkout == false && newValue < 1) return;
    if (newValue > 20) return;
    onChange(newValue);
    setShowValue(false);
    if (animationRef.current) clearAnimation();
  };

  const clearAnimation = () => {
    if (animationRef.current) {
      setShowValue(false);
      clearTimeout(animationRef.current);
      animationRef.current = undefined;
    }
  };

  useEffect(() => {
    if (value !== prevValueRef.current) {
      if (animationRef.current) clearAnimation();
      setShowValue(true);
      setInternalValue(value);

      animationRef.current = setTimeout(() => {
        setShowValue(false);
      }, 200);

      prevValueRef.current = value;

      return () => clearAnimation();
    }
  }, [value]);

  return (
    <div
      className="flex items-center justify-between gap-x-2"
      data-vaul-no-drag
    >
      <button
        data-vaul-no-drag
        type="button"
        onClick={() => changeValue(value - 1)}
        className="size-8 flex justify-center items-center bg-background border rounded-full text-foreground-muted"
      >
        <MinusIcon className="w-4" strokeWidth={"2px"} data-vaul-no-drag />
      </button>
      <div className="w-6 relative text-center" data-vaul-no-drag>
        <span className="text-lg font-bold" data-vaul-no-drag>
          {value}
        </span>
        <Transition
          data-vaul-no-drag
          className="absolute w-6 text-center transition-all"
          show={showValue}
          enter="transition duration-200 ease-in-out"
          enterFrom="bottom-0"
          enterTo="bottom-6"
          leave="transition duration-0 bottom-6"
          leaveFrom="opacity-100 bottom-6"
          leaveTo="bottom-0"
        >
          <span className="text-lg font-bold" data-vaul-no-drag>
            {internalValue}
          </span>
        </Transition>
      </div>
      <button
        type="button"
        onClick={() => changeValue(value + 1)}
        className="size-8 flex justify-center items-center bg-background border rounded-full text-foreground-muted"
      >
        <PlusIcon className="w-4" strokeWidth={"2px"} />
      </button>
    </div>
  );
};

export default QuantityInput;
